import './warnings';
import ForStractionServiceAPI, { CorporateStructureFilter, CorporateStructureLink } from 'for-straction-service-api';
import { Period, deepAssign, deepClone } from '../utils';
import { registerEntity, uniqID } from './utils';
import {
    BusinessModelCanvasItemType, Country, DBBoolean, DecisionTopicStatus, ExcelGenerationIndicatorDepthRule, ExcelGenerationValueWriteRule, GoalMetricValueType,
    MeetingInvitationPeriodType, NewsEntryEntryStatus, NewsEntryFilterPeriod, NotificationSendingTimeType, PerformanceIndicatorDimension, PerformanceIndicatorPollStatus,
    PerformanceIndicatorPollValueAggregationType, PerformanceIndicatorPollValueType, PerformanceIndicatorRelativePeriod, PerformancePerspectiveGroup, PeriodType, RequestStatus, SWOTSortMode,
    SheetForecastFileStatus, SheetForecastFileType, SortOrder, StakeholderNewsWatchFilterLevel, StrategicGoalMilestoneStatus, StrategicProgramStatus,
    StrategicProgramUpdateFrequency, SystemLanguage, SystemUserType
} from '../utils/constants';
import {
    CompanyLegalEntityType, DashboardBlockCustomTableColumnDefaultFilter, DayOfWeek, DialogDistributionChannel, DialogFlowTemplateMessageConversionRule, DialogScheduleRhytm,
    ERPAdminCostDivisionType, ForecastModelRecordDataImportPeriodType, GoalMetricValueSource, PerformanceManagementAlertActivityStatus,
    PerformanceManagementAlertDeliveryChannel, PerformanceManagementAlertEmailTitleType, PerformanceManagementAlertTriggerType, PerformanceManagementAlertType,
    StrategyExecutionSecondaryStatusUpdateType, StrategyProgressCalculationMethod, StrategyPublishingType, WorkshopQuestionnaireQuestionType, WorkshopRatingPhaseWeightedLevel,
    WorkshopStrategyImportSource
} from 'typed/api';

const
    NULL_ID = 0,
    dynamicDefaults = {},
    staticDefaults = {
        NetworkObject: {
            Data: null,
            Custom: {
                internalID: NULL_ID,
                status: RequestStatus.UNSYNCED,
                expires: null,
                changes: []
            }
        },
        NetworkArray: {
            Data: [],
            Custom: {
                status: RequestStatus.UNSYNCED,
                expires: null,
                changes: []
            }
        },
        NetworkPaginationSlice: {
            Data: [],
            Count: 0,
            NewCount: 0,
            TotalCount: 0,
            Order: SortOrder.ASCENDING,
            NextID: NULL_ID,
            Custom: {
                status: RequestStatus.UNSYNCED,
                expires: null,
                changes: []
            }
        },
        NestedNetworkPaginationSlice: {
            FatherID: NULL_ID,
            Data: [],
            Count: 0,
            TotalCount: 0,
            Order: SortOrder.ASCENDING,
            NextRow: NULL_ID,
            Custom: {
                status: RequestStatus.UNSYNCED,
                expires: null,
                changes: []
            }
        },
        NetworkObjectOf: _defaultValue => dynamicDefaults.NetworkObject,
        NetworkArrayOf: _defaultValue => dynamicDefaults.NetworkArray,
        NetworkPaginationSliceOf: _defaultValue => dynamicDefaults.NetworkPaginationSlice,
        NestedNetworkPaginationSliceOf: _defaultValue => dynamicDefaults.NestedNetworkPaginationSlice
    },

    /* eslint-disable guard-for-in */
    defaultFromValue = (value, nested) => {
        switch (true) {
            case typeof value === 'boolean':
                return false;
            case typeof value === 'number':
                return 0;
            case typeof value === 'string':
                return '';
            case Array.isArray(value):
                return [];
            case !!value?.constructor?.constructFromObject:
                const result = {};
                let instance = { ...value.constructor.prototype };
                for (const i in instance) {
                    instance[i] = [[]];
                }
                instance = value.constructor.constructFromObject(instance);
                for (const i in instance) {
                    result[i] = defaultFromValue(instance[i], true);
                    if (i === 'ID') {
                        Object.defineProperty(result, 'ID', {
                            get() {
                                return this.Custom.internalID = uniqID();
                            }
                        });
                    }
                    else if (i.includes('ID') && value.constructor.prototype[i] !== undefined && result[i] === 0) {
                        result[i] = NULL_ID;
                    }
                    else if (typeof result[i] !== 'object' && value.constructor.prototype[i] === undefined) { // check warnings
                        result[i] = null;
                    }
                }
                if (nested && !Array.isArray(instance.Data) && value.constructor !== CorporateStructureLink && value.constructor !== CorporateStructureFilter) {
                    return null;
                }

                // each API entity has this field which contains values that are not used in API but handy to store inside entity
                // by default each has request status and unsynchronized changes array
                result.Custom = deepClone(staticDefaults.NetworkObject.Custom);

                return result;
            default:
                return null;
        }
    },
    /* eslint-enable guard-for-in */

    override = (defaults, overrides) => {
        staticDefaults[defaults.EntityType] = deepAssign(staticDefaults[defaults.EntityType], overrides);
    };

// TODO is it possible to create this stuff during webpack bundling?
for (const i in ForStractionServiceAPI) {
    if (!i.includes('Api')) {
        registerEntity(ForStractionServiceAPI[i], i);
        staticDefaults[i] = defaultFromValue(ForStractionServiceAPI[i].constructFromObject({}));
        staticDefaults[i].EntityType = i;
    }
}

override(staticDefaults.SessionInformation, { Custom: { valid: true } });

override(staticDefaults.UserLocal, {
    SystemUserTypeID: SystemUserType.NORMAL_USER,
    HasAccessTo: DBBoolean.TRUE
});

override(staticDefaults.NewsEntryLightReport, {
    EntryStatusID: NewsEntryEntryStatus.RAW,
    CategoryID: 1,
    Comments: staticDefaults.NewsEntryCommentPaginationSlice,
    Contributors: staticDefaults.NewsEntryContributorPaginationSlice,
    InsightLinks: deepAssign({}, staticDefaults.NetworkArray),
    Custom: {
        revertTo: null
    }
});

staticDefaults.NewsEntryLightReportPaginationSlice = staticDefaults.NewsEntryLightReportNewsEntryReportPaginationSlice;

override(staticDefaults.OrganizationClassificationType, {
    Custom: {
        expanded: false,
        filtered: false
    }
});

override(staticDefaults.DashboardPageBlockReport, {
    TypeID: null,
    DefaultPeriodTypeID: null,
    Range: null,
    RangeStartYearOffset: 0,
    RangeStartPeriodOffset: 0,
    WidthGridCells: 1,
    HeightGridCells: 1,
    NumberFormat: '#,0.00',
    CustomTableData: staticDefaults.NetworkObject,
    Custom: {
        unconfigured: false,
        periodType: null,
        currentDate: null,
        revertTo: null
    }
});

override(staticDefaults.DashboardPageBlockCartesianSerieReport, {
    PastDimensionID: 1,
    FutureDimensionID: 1,
    AxisID: 1,
    PointCalculationID: 1
});

override(staticDefaults.DashboardPageBlockCartesianSerieGrouping, {
    StartValue: null,
    EndValue: null
});

override(staticDefaults.DashboardPageBlockComparisonColumnSerie, {
    PastDimensionID: 1,
    FutureDimensionID: 1,
    PointCalculationID: 1
});

override(staticDefaults.DashboardPageBlockComparisonBarSerie, {
    PastDimensionID: 1,
    FutureDimensionID: 1,
    PointCalculationID: 1
});

override(staticDefaults.DashboardPageBlockPieSerie, {
    PastDimensionID: 1,
    FutureDimensionID: 1,
    PointCalculationID: 1
});

override(staticDefaults.DashboardPageBlockNumberSerie, {
    PastDimensionID: 1,
    FutureDimensionID: 1,
    PointCalculationID: 1,
    NumberFormat: 'short'
});

override(staticDefaults.DashboardPageBlockSolidGaugeSerie, {
    PastDimensionID: 1,
    FutureDimensionID: 1,
    PointCalculationID: 1,
    GaugeMin: null,
    GaugeMax: null
});

override(staticDefaults.DashboardPageBlockTableRollingComparison, {
    ComparisonTargetID: 1,
    ComparisonCalculationID: 1
});

override(staticDefaults.DashboardPageBlockTableDataColumn, {
    PastDimensionID: 1,
    FutureDimensionID: 1,
    PointCalculationID: 1
});

override(staticDefaults.DashboardPageBlockTableComparisonColumn, {
    ComparisonCalculationID: 1
});

override(staticDefaults.DashboardPageBlockTableComparisonRow, {
    ComparisonCalculationID: 4
});

override(staticDefaults.DashboardPageBlockCustomTableColumn, {
    DefaultFilterID: DashboardBlockCustomTableColumnDefaultFilter.NONE
});

staticDefaults.OrganizationClassificationArray = deepAssign(staticDefaults.NetworkArray, {
    Custom: {
        filter: ''
    }
});

staticDefaults.PerformanceIndicatorFilterObject = {
    corporateStructureLink: staticDefaults.CorporateStructureLink,
    keyWord: ''
};

override(staticDefaults.PerformanceIndicatorPollSettings, {
    ValueAggregationTypeID: PerformanceIndicatorPollValueAggregationType.SUM,
    ValueTypeID: PerformanceIndicatorPollValueType.NUMERIC,
    DimensionID: PerformanceIndicatorDimension.ACTUAL,
    PeriodID: PeriodType.MONTH,
    NotificationSendingTimeTypeID: NotificationSendingTimeType.BEGINNING,
    PerformanceIndicatorPeriodTypeID: PerformanceIndicatorRelativePeriod.CURRENT,
    StatusID: PerformanceIndicatorPollStatus.DRAFT
});

staticDefaults.PerformanceIndicatorPollSettingsLightReport = deepAssign(staticDefaults.PerformanceIndicatorPollSettings, {
    Custom: {
        lastAnsweredPollBatch: staticDefaults.PerformanceIndicatorPollBatch
    }
});

staticDefaults.PollAnswerPopupVisibleBoolean = deepAssign(staticDefaults.NetworkObject, {
    Data: false
});

staticDefaults.PollAnswersNetworkArray = deepAssign(staticDefaults.NetworkArray, {
    Custom: {
        loadedPeriods: []
    }
});

override(staticDefaults.ContentWatchSettings, {
    Custom: {
        page: {
            Custom: {
                status: RequestStatus.UNSYNCED
            }
        },
        suggestions: [],
        highlight: '',
        extend: false
    }
});

staticDefaults.InterestOrganizationSegmentArray = deepAssign(staticDefaults.NetworkArray, {
    Custom: {
        currentID: NULL_ID
    }
});

override(staticDefaults.SWOTTopic, {
    Active: true
});

override(staticDefaults.SWOTTopicReport, {
    Active: true,
    VoteBatches: {
        Custom: {
            status: RequestStatus.SUCCEEDED
        }
    },
    Comments: {
        Custom: {
            status: RequestStatus.SUCCEEDED
        }
    }
});

override(staticDefaults.SWOTSessionReport, {
    Custom: {
        filter: SWOTSortMode.RATING
    }
});

override(staticDefaults.TrendTopicReport, {
    Comments: {
        Custom: {
            status: RequestStatus.SUCCEEDED
        }
    },
    Custom: {
        newComment: staticDefaults.TrendTopicComment
    }
});

override(staticDefaults.Stakeholder, {
    CountryID: Country.FI,
    NewsWatchFilterLevelID: StakeholderNewsWatchFilterLevel.MEDIUM
});

override(staticDefaults.StakeholderReport, {
    CountryID: Country.FI,
    NewsWatchFilterLevelID: StakeholderNewsWatchFilterLevel.MEDIUM
});

override(staticDefaults.DecisionReport, {
    FollowUpByUserID: null
});

staticDefaults.UserAccountSettingsEditable = {
    profileData: {
        FirstName: '',
        LastName: '',
        DefaultLanguageID: NULL_ID,
        Custom: {
            status: RequestStatus.UNSYNCED
        }
    },
    passwordData: {
        NewPassword: '',
        NewPasswordConfirmation: '',
        validationFeedback: deepAssign({}, staticDefaults.NetworkArray)
    }
};

override(staticDefaults.PerformanceIndicator, {
    NameFI: '',
    NameEN: '',
    NameSE: '',
    Name: '',
    DefaultPeriodTypeID: 2,
    FinancialFormula: '',
    ValueDependencyRuleID: 1,
    MostDetailedPeriodTypeID: PeriodType.MONTH
});

override(staticDefaults.NewsEntryFilter, {
    Period: NewsEntryFilterPeriod.AUTO
});

staticDefaults.StakeholderNewsPreviewArray = deepAssign(staticDefaults.NetworkArray, {
    Custom: {
        previewSettings: {
            brandNames: '',
            includeEnglishMarkets: false,
            newsSearch: false,
            stakeholderID: null,
            strictnessLevel: StakeholderNewsWatchFilterLevel.MEDIUM,
            typeID: null,
            webSearch: false
        }
    }
});

override(staticDefaults.ERPNetvisorIntegrationSettings, {
    Custom: {
        areSettingsValid: null
    },
    AutomaticUpdate: true
});

override(staticDefaults.StrategicProgramReport, {
    StatusID: StrategicProgramStatus.DRAFT,
    UpdateFrequencyID: StrategicProgramUpdateFrequency.WEEK,
    NoProgressTracking: false,
    RoadmapEnabled: true,
    ProgressCalculationMethodID: StrategyProgressCalculationMethod.MANUAL_STATUS_UPDATE,
    SecondaryStatusUpdateTypeID: StrategyExecutionSecondaryStatusUpdateType.NONE
});

override(staticDefaults.SheetGoogleConfiguration, {
    Custom: {
        isAuthenticated: { ...staticDefaults.NetworkObject, Data: false }
    }
});

override(staticDefaults.GoogleAnalyticsSettings, {
    Custom: {
        isAuthenticated: { ...staticDefaults.NetworkObject, Data: false },
        originalEntity: null
    }
});

override(staticDefaults.ERPFivaldiIntegrationSettings, {
    Custom: {
        isConnectionSuccessful: false
    }
});

staticDefaults.AccountingDataDimensionPreview = {
    dimension1: deepAssign({}, staticDefaults.NetworkArray),
    dimension2: deepAssign({}, staticDefaults.NetworkArray),
    dimension3: deepAssign({}, staticDefaults.NetworkArray),
    dimension4: deepAssign({}, staticDefaults.NetworkArray),
    dimension5: deepAssign({}, staticDefaults.NetworkArray),
    dimension6: deepAssign({}, staticDefaults.NetworkArray)
};

staticDefaults.SalesDataPreview = {
    customers: deepAssign({}, staticDefaults.NetworkArray),
    products: deepAssign({}, staticDefaults.NetworkArray)
};

override(staticDefaults.StrategyDescriptionReport, {
    Blocks: deepAssign({}, staticDefaults.NetworkArray),
    MemoBlocks: deepAssign({}, staticDefaults.NetworkArray),
    VisibleToAll: true,
    EditableByAll: false,
    PublishingTypeID: StrategyPublishingType.WITHOUT_REVIEW
});

override(staticDefaults.SheetForecastReport, {
    RollingHistoryMonths: 1
});

override(staticDefaults.SheetForecastFile, {
    StatusID: SheetForecastFileStatus.PENDING,
    TypeID: SheetForecastFileType.DATA_COLLECTION,
    Custom: {
        import: {
            Custom: { status: RequestStatus.UNSYNCED }
        },
        update: {
            Custom: { status: RequestStatus.UNSYNCED }
        },
        useTemplateCompany: true,
        templateID: null
    }
});

override(staticDefaults.SheetForecastFileReport, {
    StatusID: SheetForecastFileStatus.PENDING,
    TypeID: SheetForecastFileType.DATA_COLLECTION,
    Custom: {
        import: {
            Custom: { status: RequestStatus.UNSYNCED }
        },
        update: {
            Custom: { status: RequestStatus.UNSYNCED }
        },
        useTemplateCompany: true,
        templateID: null
    }
});

staticDefaults.PerformanceIndicatorAccountNumberArray = deepAssign(staticDefaults.NetworkArray, {
    Custom: {
        indicatorID: null
    }
});

override(staticDefaults.SheetGenerationSettings, {
    DimensionID: PerformanceIndicatorDimension.ACTUAL,
    PeriodID: PeriodType.MONTH,
    StartDate: new Period(PeriodType.YEAR)
});

override(staticDefaults.PerformanceIndicatorDataReport, {
    ValueDependencyRuleID: 1,
    Custom: {
        loadedRanges: []
    }
});

override(staticDefaults.PerformanceIndicatorAvailabilityReport, {
    Custom: {
        loadedRanges: [{ start: null, end: null, status: RequestStatus.SUCCEEDED }]
    }
});

override(staticDefaults.GoalMetricReport, {
    PeriodTypeID: PeriodType.MONTH,
    ValueSourceID: GoalMetricValueSource.USER_INPUT,
    GoalValueTypeID: GoalMetricValueType.ACHIEVED_EACH_PERIOD,
    InputManually: true,
    UseIncreasingDirection: true,
    PerformancePerspectiveGroupID: PerformancePerspectiveGroup.ACTIONS
});

override(staticDefaults.StrategicGoalReport, {
    Custom: {
        journeyStartDate: null,
        journeyEndDate: null,
        roadmapStartDates: {}
    }
});

override(staticDefaults.PerformanceManagementAlertReport, {
    TypeID: PerformanceManagementAlertType.TEMPLATE,
    DeliveryChannelID: PerformanceManagementAlertDeliveryChannel.EMAIL,
    ActivityStatusID: PerformanceManagementAlertActivityStatus.DRAFT,
    TriggerTypeID: PerformanceManagementAlertTriggerType.TIME_CHANGED,
    TriggerTimePeriodTypeID: PeriodType.MONTH,
    EmailTitleType: PerformanceManagementAlertEmailTitleType.PERIOD_AND_REVENUE_CHANGE,
    CustomEmailTitle: '',
    CoverLetterText: 'Ohessa viimeisimmät taloustiedot',
    Custom: {
        evaluation: staticDefaults.NetworkObject,
        preview: staticDefaults.NetworkObject,
        userLoginURLs: []
    }
});

override(staticDefaults.CompanyVirtualTeam, {
    Custom: {
        journeyStartDate: null,
        journeyEndDate: null
    }
});

override(staticDefaults.StrategicGoalMilestoneReport, {
    Custom: {
        roadmapStartDate: null,
        roadmapEndDate: null
    },
    StatusID: StrategicGoalMilestoneStatus.DRAFT,
    ProgressCalculationMethodID: StrategyProgressCalculationMethod.MANUAL_STATUS_UPDATE,
    SecondaryStatusUpdateTypeID: StrategyExecutionSecondaryStatusUpdateType.NONE,
    CanUserEditMilestone: true
});

override(staticDefaults.MeetingTemplateReport, {
    InvitationOffset: 2,
    InvitationPeriodTypeID: MeetingInvitationPeriodType.DAY
});

override(staticDefaults.IntegrationFinancialUploadBatchExcelFile, {
    DateRow: null,
    DimensionID: PerformanceIndicatorDimension.BUDGET,
    PeriodTypeID: PeriodType.MONTH,
    NegativeExpenses: true
});

override(staticDefaults.SeveraIntegrationSettingsReport, {
    IsDefault: true,
    BusinessUnitEnabled: true,
    PersonEnabled: true,
    CustomerEnabled: true,
    ProjectEnabled: true,
    ProductivityTotalHoursEnabled: true,
    ProductivityProductiveTimeEnabled: true,
    BillingUtilizationEnabled: true,
    SalesEnabled: true,
    SalesMarginEnabled: true,
    SalesMarginPerHourEnabled: true,
    SalesForecastBillingEnabled: true,
    SalesForecastCostsEnabled: true,
    SalesForecastAccrualInvoicesEnabled: true,
    SalesForecastAccrualPurchasesEnabled: true,
    SettingsBillingSourceSevera: true,
    SettingsPurchasesSourceSevera: true,
    SettingsTravelExpensesSourceSevera: true,
    SettingsWorkCostSourceSevera: true
});

override(staticDefaults.SheetExcelGenerationSettings, {
    IndicatorDepthRuleID: ExcelGenerationIndicatorDepthRule.ALL_LEVELS,
    ValueWriteRuleID: ExcelGenerationValueWriteRule.NOTHING
});

override(staticDefaults.ForecastModelReport, {
    Custom: {
        aggregation: staticDefaults.NetworkArray
    },
    DimensionID: 3
});

override(staticDefaults.ForecastModelDataEntry, {
    StatusID: 1,
    Custom: {
        template: {
            URL: '',
            Custom: staticDefaults.NetworkObject.Custom
        },
        import: {
            URL: '',
            Valid: false,
            Custom: staticDefaults.NetworkObject.Custom
        }
    }
});

override(staticDefaults.SheetExcelConfiguration, {
    Custom: {
        isAuthenticated: { ...staticDefaults.NetworkObject, Data: false }
    }
});

override(staticDefaults.Product, { IsActive: true });
override(staticDefaults.GeoArea, { IsActive: true });
override(staticDefaults.Customer, { IsActive: true });
override(staticDefaults.TrackingLevel1Item, { IsActive: true });
override(staticDefaults.TrackingLevel2Item, { IsActive: true });
override(staticDefaults.TrackingLevel3Item, { IsActive: true });
override(staticDefaults.TrackingLevel4Item, { IsActive: true });
override(staticDefaults.CompanyLegalEntity, {
    EntityTypeID: CompanyLegalEntityType.COMPANY,
    IsActive: true
});
override(staticDefaults.CompanyFunction, {
    Custom: {
        roadmapStartDate: null,
        roadmapEndDate: null,
        journeyStartDate: null,
        journeyEndDate: null
    },
    IsActive: true
});

override(staticDefaults.MeetingReport, {
    Custom: {
        invitationStatus: RequestStatus.UNSYNCED
    }
});

override(staticDefaults.NotificationFilter, { ExcludeOwn: true });

override(staticDefaults.ForesightScenarioReport, {
    MarketGrowth: 0.04,
    Custom: {
        export: {
            URL: '',
            Custom: staticDefaults.NetworkObject.Custom
        },
        calculation: {
            Custom: staticDefaults.NetworkObject.Custom
        }
    }
});

override(staticDefaults.ForesightScenarioLevelReport, {
    IsActive: true
});

override(staticDefaults.ForesightScenarioFactor, { Relative: true, Enabled: true });

override(staticDefaults.BusinessModelCanvasItemType, {
    TypeID: BusinessModelCanvasItemType.NOTE
});

override(staticDefaults.ERPAdminCostDivisionRuleReport, {
    DivisionMethodTypeID: ERPAdminCostDivisionType.EVENLY,
    SourceFilter: staticDefaults.ERPAdminCostDivisionRuleSourceFilter
});

override(staticDefaults.DashboardPageBlockPowerBIEmbed, {
    Custom: {
        embedToken: staticDefaults.PowerBIToken
    }
});

staticDefaults.PowerBIWorkspaceNetworkArray = staticDefaults.NetworkArray;

override(staticDefaults.DecisionTopicReport, {
    StatusID: DecisionTopicStatus.DRAFT
});

override(staticDefaults.ForecastModelRecordDataReport, {
    ImportPeriodTypeID: ForecastModelRecordDataImportPeriodType.ALL_PERIODS,
    ImportCustomStartPeriod: null,
    StopOnEmptyRow: true,
    ImportActual: true,
    ImportBudget: true,
    ImportForecast: true,
    Custom: {
        import: {
            Custom: staticDefaults.NetworkObject.Custom
        },
        results: staticDefaults.NetworkPaginationSlice
    }
});

override(staticDefaults.WorkshopReport, {
    Custom: {
        visited: false,
        phaseStatus: RequestStatus.UNSYNCED,
        cloningStatus: RequestStatus.UNSYNCED,
        exportStatus: RequestStatus.UNSYNCED,
        templateConversionStatus: RequestStatus.UNSYNCED,
        versions: [],
        version: null
    },
    IsCompleted: false,
    DoesFacilitatorParticipate: true,
    ContentRows: staticDefaults.NetworkArray,
    ItemHistory: staticDefaults.NetworkArray,
    ItemRatingHistory: staticDefaults.NetworkArray
});

override(staticDefaults.WorkshopPhaseReport, {
    HasDeadline: false,
    IsPublished: true,
    TemplateDefaultPublicationStatus: true
});

override(staticDefaults.WorkshopWidgetReport, {
    StatusID: null,
    DescendingRatedItemOrder: true
});

override(staticDefaults.WorkshopQuestionnaireQuestionReport, {
    QuestionTypeID: WorkshopQuestionnaireQuestionType.MULTIPLE_CHOICE
});

override(staticDefaults.PublicExternalLink, {
    LanguageID: SystemLanguage.FINNISH
});

override(staticDefaults.WorkshopRatingPhaseSourceReport, {
    WeightedLevelID: WorkshopRatingPhaseWeightedLevel.DEFAULT,
    ItemWeight: 1,
    RatedItemCount: 5
});

override(staticDefaults.DialogScheduleSettingsReport, {
    ScheduleRhytmID: DialogScheduleRhytm.MONTH,
    PeriodDaysOpen: 7,
    IsActive: true,
    UseScheduling: false,
    DistributionChannelID: DialogDistributionChannel.EMAIL
});

override(staticDefaults.DialogFlowTemplateMessageReport, {
    ConversionRuleID: DialogFlowTemplateMessageConversionRule.PHASE,
    MaxAnswersCount: 1
});

override(staticDefaults.AgendaTimeSerieRuleReport, {
    IntervalPeriodTypeID: PeriodType.WEEK,
    IntervalPeriodDay: DayOfWeek.MONDAY,
    ItemCreationDayOffset: 2
});

override(staticDefaults.WorkspaceMindmapReport, {
    IsActive: true
});

override(staticDefaults.DialogFlowMultiOptionDataSource, {
    PreviewOptions: { ...staticDefaults.NetworkObject, Data: [] }
});

override(staticDefaults.WorkshopBackgroundImage, {
    Custom: {
        base64Image: ''
    }
});

override(staticDefaults.WorkshopCloneSettings, {
    CloneCategories: true
});

override(staticDefaults.WorkshopVideoCall, {
    StartAutomatically: true,
    IsWebinar: false
});

override(staticDefaults.WorkshopStrategyImportSettings, {
    CategorySource: WorkshopStrategyImportSource.NONE,
    ItemSource: WorkshopStrategyImportSource.NONE
});

/* eslint-disable guard-for-in */
for (const i in staticDefaults) {
    ((i) => {
        staticDefaults[i].EntityType = i;
        Object.defineProperty(dynamicDefaults, i, {
            get: () => deepClone(staticDefaults[i])
        });
    })(i);
}
/* eslint-enable guard-for-in */

export default dynamicDefaults;
